import * as Dfns from 'date-fns/fp';
import { AnyActivity } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { calendarDaysAgo, formatFriendlyDay } from '@src/utils/date-utils';
import { SortBy } from './activity-feed-constants';
import { getImportanceDisplay } from './activity-feed-utils';

export type ActivityId = string;

export interface ActivityGroup {
  key: string;
  label: string;
  items: ActivityId[];
}

interface GroupHeader {
  key: string;
  label: string;
}

// Group activities into the buckets rendered as sticky section headers in the feed.
// - sortBy=most-recent: date-based buckets. Upcoming, Today, Yesterday, then one bucket per day
//   further back (e.g. "Friday, August 18th").
// - sortBy=priority: one bucket per Importance level (High, Medium, Low, N/A), in that order.
// Group order and internal item order both follow insertion order — we trust the upstream sort
// chain (see sortActivities) to hand us activities in the order we want the groups to appear.
export const groupActivities = (
  activities: AnyActivity[],
  sortBy: SortBy,
  now: Date = new Date()
): ActivityGroup[] => {
  if (sortBy === SortBy.PRIORITY) {
    return groupInto(activities, activity => ({
      key: activity.importance,
      label: getImportanceDisplay(activity.importance).label
    }));
  }
  return groupInto(activities, activity => getDateGroupFor(toLocalDate(activity.date), now));
};

// Coerce a Date | ISO-string activity.date to a Date pinned to local-time calendar day. Direct
// `new Date("2024-08-21")` parses as UTC midnight, which lands on the previous day for negative
// UTC offsets — `parseISO` on the same string honors local time.
const toLocalDate = (date: Date | string): Date =>
  typeof date === 'string' ? Dfns.parseISO(date) : date;

const groupInto = (
  activities: AnyActivity[],
  getHeader: (activity: AnyActivity) => GroupHeader
): ActivityGroup[] => {
  const groupsByKey = new Map<string, ActivityGroup>();
  for (const activity of activities) {
    const { key, label } = getHeader(activity);
    const existing = groupsByKey.get(key);
    if (existing) {
      existing.items.push(activity.id);
    } else {
      groupsByKey.set(key, { key, label, items: [activity.id] });
    }
  }
  return [...groupsByKey.values()];
};

const getDateGroupFor = (date: Date, now: Date): GroupHeader => {
  const daysDiff = calendarDaysAgo(date, now);
  if (daysDiff < 0) return { key: 'upcoming', label: 'Upcoming' };
  if (daysDiff === 0) return { key: 'today', label: 'Today' };
  if (daysDiff === 1) return { key: 'yesterday', label: 'Yesterday' };
  const startOfDate = Dfns.startOfDay(date);
  return {
    key: `d-${Dfns.format('yyyy-MM-dd', startOfDate)}`,
    label: formatFriendlyDay(startOfDate, now)
  };
};
